import { z } from 'zod';

const experimentExperienceEnum: z.ZodEnum<
	[
		'search-page',
		'quick-find',
		'search-page-space-filter',
		'quick-search',
		'advanced-search',
		'sain',
	]
> = z.enum([
	'search-page',
	'quick-find',
	'search-page-space-filter',
	'quick-search',
	'advanced-search',
	'sain',
]);

const linkedEntityGranularityEnum: z.ZodEnum<
	['ALL_MATCHING_MESSAGES', 'DEFAULT', 'FULL_THREAD', 'ONLY_LINKED_ENTITIES']
> = z.enum(['ALL_MATCHING_MESSAGES', 'DEFAULT', 'FULL_THREAD', 'ONLY_LINKED_ENTITIES']);

const layerFunctionBaseSchema = {
	layerType: z.string().startsWith('search_platform_layer_').optional(),
	entity: z.string().optional(),
	subEntity: z.string().optional(),
	hello: z.boolean().optional(),
	isJiraIssueIndexed: z.boolean().optional(),
	linkedEntityGranularity: linkedEntityGranularityEnum.optional(),
	confluenceTier: z.string().array().optional(),
	productDataRegions: z.record(z.string(), z.string().array()).optional(),
	experiences: experimentExperienceEnum.array().optional(),
	tenantIds: z.string().array().optional(),
	targetingGateId: z.string().optional(),
	providerId: z.string().optional(),
	connectorSources: z.array(z.string()).optional(),
	integrationAri: z.string().optional(),
};

const layerFunction: z.ZodObject<
	{
		layerType: z.ZodOptional<z.ZodString>;
		entity: z.ZodOptional<z.ZodString>;
		subEntity: z.ZodOptional<z.ZodString>;
		hello: z.ZodOptional<z.ZodBoolean>;
		isJiraIssueIndexed: z.ZodOptional<z.ZodBoolean>;
		linkedEntityGranularity: z.ZodOptional<
			z.ZodEnum<['ALL_MATCHING_MESSAGES', 'DEFAULT', 'FULL_THREAD', 'ONLY_LINKED_ENTITIES']>
		>;
		confluenceTier: z.ZodOptional<z.ZodArray<z.ZodString, 'many'>>;
		productDataRegions: z.ZodOptional<z.ZodRecord<z.ZodString, z.ZodArray<z.ZodString, 'many'>>>;
		experiences: z.ZodOptional<
			z.ZodArray<
				z.ZodEnum<
					[
						'search-page',
						'quick-find',
						'search-page-space-filter',
						'quick-search',
						'advanced-search',
						'sain',
					]
				>,
				'many'
			>
		>;
		tenantIds: z.ZodOptional<z.ZodArray<z.ZodString, 'many'>>;
		targetingGateId: z.ZodOptional<z.ZodString>;
		providerId: z.ZodOptional<z.ZodString>;
		connectorSources: z.ZodOptional<z.ZodArray<z.ZodString, 'many'>>;
		integrationAri: z.ZodOptional<z.ZodString>;
	},
	'strip',
	z.ZodTypeAny,
	{
		layerType?: string | undefined;
		entity?: string | undefined;
		subEntity?: string | undefined;
		hello?: boolean | undefined;
		isJiraIssueIndexed?: boolean | undefined;
		linkedEntityGranularity?:
			| 'ALL_MATCHING_MESSAGES'
			| 'DEFAULT'
			| 'FULL_THREAD'
			| 'ONLY_LINKED_ENTITIES'
			| undefined;
		confluenceTier?: string[] | undefined;
		productDataRegions?: Record<string, string[]> | undefined;
		experiences?:
			| (
					| 'search-page'
					| 'quick-find'
					| 'search-page-space-filter'
					| 'quick-search'
					| 'advanced-search'
					| 'sain'
			  )[]
			| undefined;
		tenantIds?: string[] | undefined;
		targetingGateId?: string | undefined;
		providerId?: string | undefined;
		connectorSources?: string[] | undefined;
		integrationAri?: string | undefined;
	},
	{
		layerType?: string | undefined;
		entity?: string | undefined;
		subEntity?: string | undefined;
		hello?: boolean | undefined;
		isJiraIssueIndexed?: boolean | undefined;
		linkedEntityGranularity?:
			| 'ALL_MATCHING_MESSAGES'
			| 'DEFAULT'
			| 'FULL_THREAD'
			| 'ONLY_LINKED_ENTITIES'
			| undefined;
		confluenceTier?: string[] | undefined;
		productDataRegions?: Record<string, string[]> | undefined;
		experiences?:
			| (
					| 'search-page'
					| 'quick-find'
					| 'search-page-space-filter'
					| 'quick-search'
					| 'advanced-search'
					| 'sain'
			  )[]
			| undefined;
		tenantIds?: string[] | undefined;
		targetingGateId?: string | undefined;
		providerId?: string | undefined;
		connectorSources?: string[] | undefined;
		integrationAri?: string | undefined;
	}
> = z.object(layerFunctionBaseSchema);

const dynamicConfigSchema: z.ZodPipeline<
	z.ZodEffects<z.ZodString, any, string>,
	z.ZodObject<
		{
			statsigLayerId: z.ZodString;
			layerType: z.ZodOptional<z.ZodString>;
			entity: z.ZodOptional<z.ZodString>;
			subEntity: z.ZodOptional<z.ZodString>;
			hello: z.ZodOptional<z.ZodBoolean>;
			isJiraIssueIndexed: z.ZodOptional<z.ZodBoolean>;
			linkedEntityGranularity: z.ZodOptional<
				z.ZodEnum<['ALL_MATCHING_MESSAGES', 'DEFAULT', 'FULL_THREAD', 'ONLY_LINKED_ENTITIES']>
			>;
			confluenceTier: z.ZodOptional<z.ZodArray<z.ZodString, 'many'>>;
			productDataRegions: z.ZodOptional<z.ZodRecord<z.ZodString, z.ZodArray<z.ZodString, 'many'>>>;
			experiences: z.ZodOptional<
				z.ZodArray<
					z.ZodEnum<
						[
							'search-page',
							'quick-find',
							'search-page-space-filter',
							'quick-search',
							'advanced-search',
							'sain',
						]
					>,
					'many'
				>
			>;
			tenantIds: z.ZodOptional<z.ZodArray<z.ZodString, 'many'>>;
			targetingGateId: z.ZodOptional<z.ZodString>;
			providerId: z.ZodOptional<z.ZodString>;
			connectorSources: z.ZodOptional<z.ZodArray<z.ZodString, 'many'>>;
			integrationAri: z.ZodOptional<z.ZodString>;
		},
		'strip',
		z.ZodTypeAny,
		{
			statsigLayerId: string;
			layerType?: string | undefined;
			entity?: string | undefined;
			subEntity?: string | undefined;
			hello?: boolean | undefined;
			isJiraIssueIndexed?: boolean | undefined;
			linkedEntityGranularity?:
				| 'ALL_MATCHING_MESSAGES'
				| 'DEFAULT'
				| 'FULL_THREAD'
				| 'ONLY_LINKED_ENTITIES'
				| undefined;
			confluenceTier?: string[] | undefined;
			productDataRegions?: Record<string, string[]> | undefined;
			experiences?:
				| (
						| 'search-page'
						| 'quick-find'
						| 'search-page-space-filter'
						| 'quick-search'
						| 'advanced-search'
						| 'sain'
				  )[]
				| undefined;
			tenantIds?: string[] | undefined;
			targetingGateId?: string | undefined;
			providerId?: string | undefined;
			connectorSources?: string[] | undefined;
			integrationAri?: string | undefined;
		},
		{
			statsigLayerId: string;
			layerType?: string | undefined;
			entity?: string | undefined;
			subEntity?: string | undefined;
			hello?: boolean | undefined;
			isJiraIssueIndexed?: boolean | undefined;
			linkedEntityGranularity?:
				| 'ALL_MATCHING_MESSAGES'
				| 'DEFAULT'
				| 'FULL_THREAD'
				| 'ONLY_LINKED_ENTITIES'
				| undefined;
			confluenceTier?: string[] | undefined;
			productDataRegions?: Record<string, string[]> | undefined;
			experiences?:
				| (
						| 'search-page'
						| 'quick-find'
						| 'search-page-space-filter'
						| 'quick-search'
						| 'advanced-search'
						| 'sain'
				  )[]
				| undefined;
			tenantIds?: string[] | undefined;
			targetingGateId?: string | undefined;
			providerId?: string | undefined;
			connectorSources?: string[] | undefined;
			integrationAri?: string | undefined;
		}
	>
> = z
	.string()
	.transform((str, ctx) => {
		try {
			return JSON.parse(str);
		} catch (e) {
			ctx.addIssue({ code: 'custom', message: 'Invalid JSON' });
			return z.NEVER;
		}
	})
	.pipe(
		z.object({
			...layerFunctionBaseSchema,
			statsigLayerId: z.string().startsWith('search_platform_'),
		}),
	);

const mutuallyExclusiveDynamicConfigSchema = z
	.string()
	.transform((str, ctx) => {
		try {
			return JSON.parse(str);
		} catch (e) {
			ctx.addIssue({ code: 'custom', message: 'Invalid JSON' });
			return z.NEVER;
		}
	})
	.pipe(
		z.object({
			...layerFunctionBaseSchema,
			layerId: z.string(),
			controlId: z.string().optional(),
		}),
	);

export const dynamicExperimentsSchema: z.ZodEffects<
	z.ZodEffects<
		z.ZodArray<z.ZodString, 'many'>,
		{
			statsigLayerId: string;
			layerType?: string | undefined;
			entity?: string | undefined;
			subEntity?: string | undefined;
			hello?: boolean | undefined;
			isJiraIssueIndexed?: boolean | undefined;
			linkedEntityGranularity?:
				| 'ALL_MATCHING_MESSAGES'
				| 'DEFAULT'
				| 'FULL_THREAD'
				| 'ONLY_LINKED_ENTITIES'
				| undefined;
			confluenceTier?: string[] | undefined;
			productDataRegions?: Record<string, string[]> | undefined;
			experiences?:
				| (
						| 'search-page'
						| 'quick-find'
						| 'search-page-space-filter'
						| 'quick-search'
						| 'advanced-search'
						| 'sain'
				  )[]
				| undefined;
			tenantIds?: string[] | undefined;
			targetingGateId?: string | undefined;
			providerId?: string | undefined;
			connectorSources?: string[] | undefined;
			integrationAri?: string | undefined;
		}[],
		string[]
	>,
	{
		statsigLayerId: string;
		layerType?: string | undefined;
		entity?: string | undefined;
		subEntity?: string | undefined;
		hello?: boolean | undefined;
		isJiraIssueIndexed?: boolean | undefined;
		linkedEntityGranularity?:
			| 'ALL_MATCHING_MESSAGES'
			| 'DEFAULT'
			| 'FULL_THREAD'
			| 'ONLY_LINKED_ENTITIES'
			| undefined;
		confluenceTier?: string[] | undefined;
		productDataRegions?: Record<string, string[]> | undefined;
		experiences?:
			| (
					| 'search-page'
					| 'quick-find'
					| 'search-page-space-filter'
					| 'quick-search'
					| 'advanced-search'
					| 'sain'
			  )[]
			| undefined;
		tenantIds?: string[] | undefined;
		targetingGateId?: string | undefined;
		providerId?: string | undefined;
		connectorSources?: string[] | undefined;
		integrationAri?: string | undefined;
	}[],
	string[]
> = z
	.array(z.string())
	.transform((arrayJSONStrings) => {
		return arrayJSONStrings
			.map((jsonStr) => dynamicConfigSchema.safeParse(jsonStr).data)
			.flatMap((dynamicConfig) => (dynamicConfig ? dynamicConfig : []));
	})
	.refine((dynamicConfig) => dynamicConfig.length); // The schema is invalid if all JSON strings are invalid

export const mutuallyExclusiveBackendSearchLogicParametersSchema: z.ZodEffects<
	z.ZodEffects<
		z.ZodArray<z.ZodString, 'many'>,
		{
			layerId: string;
			layerType?: string | undefined;
			entity?: string | undefined;
			subEntity?: string | undefined;
			hello?: boolean | undefined;
			isJiraIssueIndexed?: boolean | undefined;
			linkedEntityGranularity?:
				| 'ALL_MATCHING_MESSAGES'
				| 'DEFAULT'
				| 'FULL_THREAD'
				| 'ONLY_LINKED_ENTITIES'
				| undefined;
			confluenceTier?: string[] | undefined;
			productDataRegions?: Record<string, string[]> | undefined;
			experiences?:
				| (
						| 'search-page'
						| 'quick-find'
						| 'search-page-space-filter'
						| 'quick-search'
						| 'advanced-search'
						| 'sain'
				  )[]
				| undefined;
			tenantIds?: string[] | undefined;
			targetingGateId?: string | undefined;
			providerId?: string | undefined;
			connectorSources?: string[] | undefined;
			integrationAri?: string | undefined;
			controlId?: string | undefined;
		}[],
		string[]
	>,
	{
		layerId: string;
		layerType?: string | undefined;
		entity?: string | undefined;
		subEntity?: string | undefined;
		hello?: boolean | undefined;
		isJiraIssueIndexed?: boolean | undefined;
		linkedEntityGranularity?:
			| 'ALL_MATCHING_MESSAGES'
			| 'DEFAULT'
			| 'FULL_THREAD'
			| 'ONLY_LINKED_ENTITIES'
			| undefined;
		confluenceTier?: string[] | undefined;
		productDataRegions?: Record<string, string[]> | undefined;
		experiences?:
			| (
					| 'search-page'
					| 'quick-find'
					| 'search-page-space-filter'
					| 'quick-search'
					| 'advanced-search'
					| 'sain'
			  )[]
			| undefined;
		tenantIds?: string[] | undefined;
		targetingGateId?: string | undefined;
		providerId?: string | undefined;
		connectorSources?: string[] | undefined;
		integrationAri?: string | undefined;
		controlId?: string | undefined;
	}[],
	string[]
> = z
	.array(z.string())
	.transform((arrayJSONStrings) => {
		return arrayJSONStrings
			.map((jsonStr) => mutuallyExclusiveDynamicConfigSchema.safeParse(jsonStr).data)
			.flatMap((dynamicConfig) => (dynamicConfig ? dynamicConfig : []));
	})
	.refine((dynamicConfig) => dynamicConfig.length); // The schema is invalid if all JSON strings are invalid

export type DynamicConfig = z.infer<typeof dynamicConfigSchema>;
export type ExperimentExperience = z.infer<typeof experimentExperienceEnum>;
export type LinkedEntityGranularity = z.infer<typeof linkedEntityGranularityEnum>;
export type ExperimentExperiences = z.infer<typeof dynamicConfigSchema>['experiences'];
export type LayerFunction = z.infer<typeof layerFunction>;
export type DynamicExperiments = z.infer<typeof dynamicExperimentsSchema>;
export type MutuallyExclusiveBackendSearchLogicParameters = z.infer<
	typeof mutuallyExclusiveBackendSearchLogicParametersSchema
>;
